import { useEffect, useRef, type CSSProperties } from 'react';

/** Cream lines on transparent; reads as a faint texture over the page background. */
export const GEOMETRIC_PATTERN_URL =
  'https://media.multihub.io/property/6767906449167598/01a17eb3-e87e-762a-a776-fea06b73b32d.webp';

/** Fraction of scroll distance the pattern drifts; higher = stronger parallax. */
const PARALLAX_FACTOR = 0.3;

const wrapStyle: CSSProperties = {
  position: 'absolute',
  insetInline: 0,
  top: '-20%',
  height: '140%',
  opacity: 0.25,
  backgroundImage: `url(${GEOMETRIC_PATTERN_URL})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  willChange: 'transform',
};

/** Render as the first child of a `relative overflow-hidden` section. */
export function GeometricPatternBackground() {
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const section = wrap?.parentElement?.parentElement;
    if (!wrap || !section) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const scrolled = -section.getBoundingClientRect().top;
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      wrap.style.transform = `translateY(${scrolled * PARALLAX_FACTOR}px)`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div ref={wrapRef} style={wrapStyle} />
    </div>
  );
}
